<template>
  <ModalComp
    :is-visible="isVisible"
    @update:is-visible="$emit('update:isVisible', $event)"
    size="sm"
    :hide-footer="true"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
          <i class="fas fa-user text-blue-600 text-lg"></i>
        </div>
        <div>
          <h3 class="text-xl font-semibold text-gray-800">Mon profil</h3>
          <p class="text-sm text-gray-600">Informations et liens utiles</p>
        </div>
      </div>
    </template>

    <template #body>
      <div class="space-y-6">
        <!-- Informations personnelles -->
        <div v-if="isBusy" class="text-center py-4">
          <i class="fas fa-spinner fa-spin text-blue-600 text-xl"></i>
          <p class="text-sm text-gray-600 mt-2">Chargement des informations...</p>
        </div>
        
        <template v-else>
          <div class="space-y-4">
            <h4 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <i class="fas fa-user-circle text-blue-600"></i>
              Informations personnelles
            </h4>
            
            <div class="space-y-3">
              <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <i class="fas fa-envelope text-gray-500 w-5"></i>
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-700">Email</p>
                  <p class="text-sm text-gray-600">{{ displayVendeur.email || 'Non disponible' }}</p>
                </div>
              </div>
              
              <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <i class="fas fa-phone text-gray-500 w-5"></i>
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-700">Téléphone fixe</p>
                  <p class="text-sm text-gray-600">{{ displayVendeur.telephone || 'Non disponible' }}</p>
                </div>
              </div>
              
              <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <i class="fas fa-mobile-alt text-gray-500 w-5"></i>
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-700">Téléphone mobile</p>
                  <p class="text-sm text-gray-600">{{ displayVendeur.mobile || 'Non disponible' }}</p>
                </div>
              </div>
              
              <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <i class="fas fa-map-marker-alt text-gray-500 w-5"></i>
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-700">Adresse</p>
                  <p class="text-sm text-gray-600">{{ displayVendeur.adresse || 'Non disponible' }}, {{ displayVendeur.code_postal || 'Non disponible' }} {{ displayVendeur.ville || 'Non disponible' }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Liens utiles -->
          <div class="space-y-4">
            <h4 class="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <i class="fas fa-external-link-alt text-blue-600"></i>
              Liens utiles
            </h4>
            <a href="#" @click.prevent="contactUs" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <i class="fas fa-headset text-gray-500 w-5"></i>
              <span class="text-sm font-medium text-gray-700">Contactez nous</span>
            </a>
            <div class="space-y-2">
              <a href="https://knowledge.efashion-paris.com/vendors" target="_blank" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <i class="fas fa-book text-gray-500 w-5"></i>
                <span class="text-sm font-medium text-gray-700">Guide Efashion</span>
              </a>
              <a href="https://landingpage.efashion-paris.com/fr/conditions-g%C3%A9n%C3%A9rales-dutilisation-grossistes-vendeurs" target="_blank" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <i class="fas fa-file-contract text-gray-500 w-5"></i>
                <span class="text-sm font-medium text-gray-700">CGUV</span>
              </a>
            </div>
          </div>
          
          <!-- Déconnexion -->
          <div class="pt-4 border-t border-gray-200">
            <button @click="seDeconnecter" class="w-full flex items-center gap-2 p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-red-600">
              <i class="fas fa-sign-out-alt w-4"></i>
              <span class="text-xs font-medium">Déconnexion</span>
            </button>
          </div>
        </template>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { ModalComp } from '@/components'
import { useAuthStore } from '@/stores/auth'
import { getVendeurGeneralInfo } from '@/api/vendeur'

interface Props {
  isVisible: boolean
  isLoading: boolean
  vendeurData: any
}

interface Emits {
  (e: 'update:isVisible', value: boolean): void
  (e: 'show-contact-modal'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const authStore = useAuthStore()

// Local state for data loading on mobile
const isLoadingLocal = ref(false)
const LOCAL_STORAGE_KEY = 'vendeur_info_header'

type VendeurMini = {
  email?: string
  telephone?: string
  mobile?: string
  adresse?: string
  code_postal?: string
  ville?: string
}

const DEFAULT_VENDEUR: VendeurMini = {
  email: undefined,
  telephone: undefined,
  mobile: undefined,
  adresse: undefined,
  code_postal: undefined,
  ville: undefined
}

const displayVendeur = ref<VendeurMini>({ ...DEFAULT_VENDEUR, ...(props.vendeurData || {}) })

const isBusy = ref(false)
watch(
  () => props.isVisible,
  async (open) => {
    if (!open) return
    isBusy.value = true
    isLoadingLocal.value = true
    try {
      // 1) Try from localStorage (already filled by navbar on desktop or earlier)
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY)
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as VendeurMini
          if (Object.prototype.hasOwnProperty.call(parsed, 'ville')) {
            displayVendeur.value = { ...DEFAULT_VENDEUR, ...parsed }
            return
          }
        } catch {
          // ignore parse error and fall back to API
        }
      }

      // 2) Fallback: fetch from API
      const profile = await getVendeurGeneralInfo()
      const storeAddress = (profile as any).adresses?.find((addr: any) => addr.type === 2) || (profile as any).adresses?.[0]
      displayVendeur.value = {
        email: (profile as any).email,
        telephone: storeAddress?.telephone,
        mobile: storeAddress?.mobile,
        adresse: storeAddress?.adresse,
        code_postal: storeAddress?.codePostal,
        ville: storeAddress?.ville
      }
    } catch (e) {
      // keep defaults
      // Erreur silencieuse - les infos vendeur n'ont pas été chargées
    } finally {
      isLoadingLocal.value = false
      isBusy.value = false
    }
  },
  { immediate: false }
)

function seDeconnecter() {
  authStore.logout();
}

function contactUs() {
  emit('show-contact-modal')
  emit('update:isVisible', false)
}
</script>
